/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef, useContext } from "react";
import { HashLink } from "react-router-hash-link";
import { Button, CloseIconThin } from "@cmsgov/ds-medicare-gov";
import stickybits from "stickybits";
import { cssTransition, toast, ToastContainer } from "react-toastify";
import { v1 as uuidv1 } from "uuid";
import { useTranslate } from "../helpers";
import { isWindowMobileOuterWidth } from "../helpers/objectUtilities";
import classNames from "classnames";
import { AppContext } from "../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";

interface StickyTrayProps {
  className?: string;
  errorMessage?: string | React.ReactNode;
  heading: string;
  id?: string;
  items: {
    name: string;
    id: number | string;
    subHeader?: string | React.ReactNode;
  }[];
  isValid?: boolean;
  linkForSubmit?: string;
  maxItems?: number;
  mobileOpenButtonText?: string;
  onMobileToggleVisibility?: (visibility: boolean) => void;
  noItemsText?: string;
  onRemoveItem: (id: number | string) => void;
  onSubmit: () => void;
  pageLoading?: boolean;
  screenReaderText?: string;
  submitDisabled?: boolean;
  submitText: string;
  submitClass?: string;
  warningMessage?: string | React.ReactNode;
  notStickyOnMobile?: boolean;
}

const StickyTray: React.FunctionComponent<StickyTrayProps> = ({
  errorMessage,
  heading,
  id,
  isValid = true,
  items,
  linkForSubmit,
  maxItems = 3,
  mobileOpenButtonText,
  noItemsText,
  notStickyOnMobile,
  onMobileToggleVisibility = (): void => {},
  onRemoveItem,
  onSubmit,
  pageLoading,
  screenReaderText,
  submitDisabled,
  submitText,
  submitClass,
  warningMessage,
  ...props
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const TOAST_DURATION = 5000;
  const [toastId, setToastId] = useState("");
  const [isOpen, _setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  const setIsOpen = (value: boolean): void => {
    isOpenRef.current = value;
    _setIsOpen(value);
  };
  const [tabIndex, setTabIndex] = useState(0);
  const [stickyBit, setStickyBit] = useState<
    { update: () => void } | undefined
  >(undefined);

  const toggleVisibility = (): void => {
    const visibility = !isOpen;
    setIsOpen(visibility);
    onMobileToggleVisibility(visibility);
  };

  let lastWindowArea = window.outerHeight * window.outerWidth;

  const onResize = (): void => {
    const area = window.outerHeight * window.outerWidth;
    if (lastWindowArea !== area) {
      lastWindowArea = area;

      setTabIndex(
        !isWindowMobileOuterWidth(window) || isOpenRef.current ? 0 : -1
      );

      if (stickyBit) {
        stickyBit.update();
        window.scrollBy(0, 1);
      }
    }
  };

  useEffect(() => {
    setToastId(uuidv1());
    const identifier = id ? `#${id}` : ".js-stickybits";
    setStickyBit(stickybits(identifier, { verticalPosition: "bottom" }));
    window.scrollBy(0, 1);
    window.addEventListener("resize", onResize);
    return (): void => {
      window.removeEventListener("resize", onResize);
      document.body.classList.remove("no-scroll-on-mobile");
    };
  }, []);

  useEffect(() => {
    if (stickyBit) {
      stickyBit.update();
      window.scrollBy(0, 1);
    }
  }, [pageLoading]);

  useEffect(() => {
    toast.dismiss();
  }, [items.length]);

  useEffect(() => {
    if (isOpen) {
      if (!notStickyOnMobile) {
        document.body.classList.add("no-scroll-on-mobile");
      }
      setTabIndex(0);
    } else {
      document.body.classList.remove("no-scroll-on-mobile");
      setTabIndex(isWindowMobileOuterWidth(window) ? -1 : 0);
    }
  }, [isOpen]);

  errorMessage &&
    toast(
      <div className="ds-u-fill--error-lightest ds-u-padding--2">
        {errorMessage}
      </div>,
      {
        toastId,
        containerId: "stickyTrayToastContainer",
        autoClose: TOAST_DURATION,
        className: "ds-u-padding--0",
        type: toast.TYPE.ERROR,
      }
    );

  warningMessage &&
    toast(
      <div className="ds-u-fill--primary-alt-lightest ds-u-padding--2">
        {warningMessage}
      </div>,
      {
        toastId,
        containerId: "stickyTrayToastContainer",
        autoClose: TOAST_DURATION,
        className: "ds-u-padding--0",
      }
    );

  const emptyItems: string[] = [];
  emptyItems.length = maxItems - Math.min(items.length, maxItems);
  emptyItems.fill("");

  return (
    <>
      {!notStickyOnMobile && (
        <div
          className={`StickyTray__background${isOpen ? "" : " closed"}`}
          onClick={(): void => setIsOpen(false)}
        />
      )}
      <div
        {...props}
        id={id}
        className={`StickyTray js-stickybits ${
          props.className ? props.className : ""
        }${isOpen ? "" : " closed"}${
          notStickyOnMobile ? " not-sticky-on-mobile" : ""
        }`}
        role="region"
        aria-live="polite"
        data-testid="StickyTray"
        style={{ border: "none" }}
      >
        <ToastContainer
          enableMultiContainer
          containerId="stickyTrayToastContainer"
          autoClose={TOAST_DURATION}
          closeOnClick={false}
          draggable={false}
          hideProgressBar
          newestOnTop={false}
          position={toast.POSITION.BOTTOM_CENTER}
          rtl={false}
          className="StickyTray__toast"
          transition={cssTransition({
            enter: "zoomIn",
            exit: "zoomOut",
            duration: 400,
          })}
        />
        <div className="StickyTray__body">
          <div className="StickyTray__content">
            <div className="StickyTray__flex">
              <div
                className="StickyTray__header"
                id={id ? `${id}-sticky-tray-heading` : undefined}
                tabIndex={-1}
                onClick={(): void => {
                  if (isOpen) {
                    setIsOpen(false);
                  }
                }}
              >
                <div className="StickyTray__heading">
                  <span>{heading}</span>
                  <span className="StickyTray__mobile_close">
                    <Button
                      className="ds-u-padding--2"
                      variation="ghost"
                      onDark
                      onClick={() => {
                        dispatch({
                          type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                          settings: {
                            button: {
                              buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                              buttonType: AnalyticsButtonType.BUTTON,
                              text: t("close"),
                            },
                          },
                        });
                        toggleVisibility();
                      }}
                      tabIndex={tabIndex}
                      aria-label={t("close")}
                    >
                      <CloseIconThin />
                    </Button>
                  </span>
                </div>
                {!!items.length && (
                  <Button
                    onDark
                    className="StickyTray__mobile_open"
                    onClick={() => {
                      dispatch({
                        type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                        settings: {
                          button: {
                            buttonStyle: AnalyticsButtonStyle.SECONDARY,
                            buttonType: AnalyticsButtonType.BUTTON,
                            text: mobileOpenButtonText
                              ? mobileOpenButtonText
                              : "Open",
                          },
                        },
                      });
                      toggleVisibility();
                    }}
                  >
                    {mobileOpenButtonText ? mobileOpenButtonText : "Open"}
                  </Button>
                )}
                {screenReaderText && (
                  <span className="ds-u-visibility--screen-reader">
                    {screenReaderText}
                  </span>
                )}
              </div>
              {items.length ? (
                <ul
                  className={`StickyTray__list${isOpen ? "" : " closed"}`}
                  role="list"
                >
                  {items.map(item => (
                    <li
                      className="StickyTray__list_item StickyTrayListItem"
                      key={item.id}
                      role="listitem"
                    >
                      <span
                        className={classNames("StickyTrayListItem__name", {
                          "ds-u-padding-bottom--0": item.subHeader,
                        })}
                      >
                        {item.name}
                      </span>
                      {item.subHeader && (
                        <div className="StickyTrayListItem__sub-header">
                          {item.subHeader}
                        </div>
                      )}

                      <Button
                        variation="ghost"
                        onDark
                        className="StickyTray__remove StickyTrayListItem__remove"
                        onClick={(): void => {
                          if (items.length === 1) {
                            setIsOpen(false);
                          }
                          onRemoveItem(item.id);
                          dispatch({
                            type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                            settings: {
                              button: {
                                buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                                buttonType: AnalyticsButtonType.BUTTON,
                                text: `${t("remove")} ${item.name}`,
                              },
                            },
                          });
                        }}
                        aria-label={`${t("remove")} ${item.name}`}
                        tabIndex={tabIndex}
                      >
                        <CloseIconThin />
                      </Button>
                    </li>
                  ))}
                  {emptyItems.map((_item, i) => (
                    <li
                      className="StickyTray__list_item StickyTray__list_item_empty"
                      key={`open-spot-${i}`}
                      aria-hidden
                    >
                      &nbsp;
                    </li>
                  ))}
                </ul>
              ) : null}
              {!items.length ? (
                <span className="ds-u-margin-right--2">{noItemsText}</span>
              ) : null}
              {linkForSubmit ? (
                <HashLink
                  to={linkForSubmit}
                  className={`StickyTray__action ds-c-button ds-c-button--solid ds-c-button--on-dark ${submitClass}`}
                  onClick={e => {
                    if (!isValid) {
                      e.preventDefault();
                    }
                    onSubmit();
                  }}
                >
                  {submitText}
                </HashLink>
              ) : (
                <Button
                  variation="solid"
                  onDark
                  isAlternate
                  data-testid="sticky-tray-submit-button"
                  className={`StickyTray__action ${submitClass}`}
                  disabled={submitDisabled}
                  onClick={() => {
                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.DEFAULT,
                          buttonType: AnalyticsButtonType.SUBMIT,
                          text: submitText,
                        },
                      },
                    });
                    onSubmit();
                  }}
                >
                  {submitText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StickyTray;
